import { ArrowRight, CheckCircle2 } from "lucide-react";

import { Badge } from "../../ui/badge.js";
import { SpecularButton } from "../../ui/specular-button.js";
import { OnboardingLinkPanel } from "./OnboardingLinkPanel.js";
import type { OnboardingRecord } from "./OnboardingSection.js";

export type OnboardingLinkRecord = Pick<
  OnboardingRecord,
  "status" | "activatedAt" | "accessLinkSentAt" | "accessLinkSentTo" | "accessTokenExpiresAt"
>;

// Shown in place of the card's Save action once the hire exists, so HR can send
// the onboarding link from the same card instead of going looking for the
// Onboarding section. The record arrives a tick after the create POST — until
// then the send row is replaced by a placeholder line rather than a disabled
// control that would read as "sending is not allowed".
export function NewHireCreatedPanel({ employeeId, employeeCode, name, personalEmail, record, onLinkSent, onOpenProfile }: {
  employeeId: string;
  employeeCode: string;
  name: string;
  personalEmail: string;
  record: OnboardingLinkRecord | null;
  onLinkSent: () => Promise<unknown>;
  onOpenProfile: () => void;
}) {
  return (
    <div className="mt-4 rounded-md border border-[var(--color-success-border)] bg-[var(--color-success-bg-subtle)] p-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <CheckCircle2 size={16} className="shrink-0 text-[var(--color-success-fg)]" />
        <span className="min-w-0 flex-1 text-[13px] font-semibold">{name} created</span>
        {employeeCode && <Badge tone="success">{employeeCode}</Badge>}
      </div>
      <p className="mt-1 text-xs text-surface-600">
        Send them the onboarding link to collect the rest — identity documents, bank details, emergency
        contacts and references — straight from the employee.
      </p>

      <div className="mt-2 rounded-md bg-[var(--color-surface-0)] px-3">
        {record
          ? <OnboardingLinkPanel employeeId={employeeId} personalEmail={personalEmail} record={record} onSent={onLinkSent} />
          : <p className="py-3 text-xs text-surface-500">Loading the onboarding record…</p>}
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <SpecularButton size="sm" onClick={onOpenProfile}>
          Open full profile <ArrowRight size={12} />
        </SpecularButton>
        <span className="text-[11px] text-surface-500">Keep editing below and save again, or open the saved profile.</span>
      </div>
    </div>
  );
}
